import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  sortAndPaginateData,
  TIER2_DASHBOARD_SORT_CONFIG,
} from 'store/reportsManager/helpers'
import {
  ORDER_BY,
  TABLE_PAGE_SIZE,
  TABLE_URL_PARAMS,
  TIER2_DASHBOARD_SORT_KEYS,
} from 'store/reportsManager/constants'
import { selectors as reportsSelectors } from 'store/reportsManager'
import { useValidatedTableParams } from 'hooks/useValidatedTableParams'
import { createTableValidators } from 'hooks/useValidatedTableParams/createValidators'
import { calculatePaginationValues, shouldShowPagination } from './helpers'
import AdminK8Tier2MultiSiteView from './component'

const AdminK8Tier2MultiSiteViewContainer = ({
  isFetching,
  tier2DashboardSummary,
  tier2DashboardSites,
}) => {
  // Determine if pagination is needed
  const needsPagination = shouldShowPagination(
    tier2DashboardSites,
    TABLE_PAGE_SIZE,
  )

  // Create validators with defaults and validation logic
  const validSortKeys = useMemo(
    () => Object.keys(TIER2_DASHBOARD_SORT_CONFIG),
    [],
  )

  const validators = useMemo(
    () =>
      createTableValidators({
        validSortKeys,
        defaultSortKey: TIER2_DASHBOARD_SORT_KEYS.SCHOOLS,
        defaultOrderBy: ORDER_BY.ASC,
      }),
    [validSortKeys],
  )

  // Managed param keys - only params that should be in URL
  // Use TABLE_URL_PARAMS constants - hook converts to validator keys internally
  const managedParamKeys = useMemo(() => {
    const keys = [TABLE_URL_PARAMS.SORT_KEY, TABLE_URL_PARAMS.ORDER_BY]
    if (needsPagination) {
      keys.push(TABLE_URL_PARAMS.CURRENT_PAGE) // pageSize is always TABLE_PAGE_SIZE, not in URL
    }
    return keys
  }, [needsPagination])

  // Determine if managedParamKeys is settled (data is hydrated and stable)
  // When tier2DashboardSites is an array, we can compute needsPagination accurately
  const isSettled = Array.isArray(tier2DashboardSites)

  const { validated, updateUrlParams } = useValidatedTableParams({
    validators,
    managedParamKeys,
    isSettled,
  })

  // Use validated values directly (no derivation needed!)
  // New hook returns values with validator keys (not TABLE_URL_PARAMS constants)
  // Provide fallbacks from validators for initial render when validated is empty
  const sortKey = validated.sortKey ?? validators.sortKey?.default
  const orderBy = validated.orderBy ?? validators.orderBy?.default
  const currentPage = validated.currentPage ?? validators.currentPage?.default
  const pageSize = TABLE_PAGE_SIZE // Always use TABLE_PAGE_SIZE, not in URL

  // Sort and paginate the data in the container using useMemo
  const sortedAndPaginatedSites = useMemo(() => {
    if (!Array.isArray(tier2DashboardSites)) {
      return tier2DashboardSites // Return null or {} from createSelectorState
    }

    return sortAndPaginateData({
      data: tier2DashboardSites,
      sortConfig: TIER2_DASHBOARD_SORT_CONFIG,
      sortKey,
      orderBy,
      currentPage,
      pageSize,
    })
  }, [tier2DashboardSites, sortKey, orderBy, currentPage, pageSize])

  // Compute totals for pagination
  const totalItems = Array.isArray(tier2DashboardSites)
    ? tier2DashboardSites.length
    : 0

  // Calculate pagination display values
  const {
    totalPages,
    siteStartCount,
    siteEndCount,
  } = calculatePaginationValues({
    totalItems,
    currentPage,
    pageSize,
    paginatedItemsLength: sortedAndPaginatedSites?.length,
  })

  // Clamp currentPage to totalPages if out of bounds and update URL
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      updateUrlParams({ currentPage: totalPages }, 'replace')
    }
  }, [currentPage, totalPages, updateUrlParams])

  // Handler for pagination/sort changes - updates URL query params
  // Note: Order in updates object doesn't matter - updateUrlParams rebuilds URL in managedParamKeys order
  // Use validator keys (not TABLE_URL_PARAMS constants)
  // Note: Not using useCallback to match K8Tier2LessonCompletion pattern - function is always defined
  const handlePaginationSortChange = ({ newSortKey, newOrderBy, newPage }) => {
    if (!updateUrlParams) {
      console.error('❌ updateUrlParams is not available!')
      return
    }

    const updates = {}
    if (newSortKey !== undefined) {
      updates.sortKey = newSortKey
    }
    if (newOrderBy !== undefined) {
      updates.orderBy = newOrderBy
    }
    if (newPage !== undefined) {
      updates.currentPage = newPage // Hook normalizes to number, no need to convert to string
    }

    updateUrlParams(updates, 'push')
  }

  return (
    <AdminK8Tier2MultiSiteView
      currentPage={currentPage}
      handlePaginationSortChange={handlePaginationSortChange}
      isFetching={isFetching}
      needsPagination={needsPagination}
      orderBy={orderBy}
      siteEndCount={siteEndCount}
      siteStartCount={siteStartCount}
      sites={sortedAndPaginatedSites}
      sortKey={sortKey}
      summary={tier2DashboardSummary}
      totalItems={totalItems}
      totalPages={totalPages}
    />
  )
}

AdminK8Tier2MultiSiteViewContainer.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  tier2DashboardSites: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        siteId: PropTypes.number.isRequired,
        schoolName: PropTypes.string,
        smallGroupsCreated: PropTypes.number,
        groupsThatHaventCompletedLesson: PropTypes.number,
        lessonsCompleted: PropTypes.number,
      }),
    ),
    PropTypes.object, // {} when fetching/error (prevent refetching)
    PropTypes.oneOf([null]), // null when not hydrated (should fetch)
  ]).isRequired,
  tier2DashboardSummary: PropTypes.shape({
    groupsThatHaventCompletedLesson: PropTypes.number.isRequired,
    lessonsCompleted: PropTypes.number.isRequired,
    numberOfSites: PropTypes.number.isRequired,
    lastUpdatedAt: PropTypes.string, // Formatted string like "Updated 2 hours ago"
    smallGroupsCreated: PropTypes.number.isRequired,
  }).isRequired,
}

const mapStateToProps = state => {
  const isFetching =
    state.reportsManager?.admin?.k8?.tier2?.dashboard?.isFetching || false
  const tier2DashboardSites = reportsSelectors.selectTier2DashboardSites(state)
  const tier2DashboardSummary = reportsSelectors.selectTier2DashboardSummary(
    state,
  )
  return {
    isFetching,
    tier2DashboardSummary,
    tier2DashboardSites,
  }
}

export default connect(
  mapStateToProps,
  null,
)(AdminK8Tier2MultiSiteViewContainer)
